import { formatInTimeZone } from "date-fns-tz";
import { BrazilDate } from "./BrazilDate";
import { isoWeekdayFromISO } from "./controleProducaoDates";

export type TurnoProducao = "dia" | "noite";

export type HorariosTurnoProducao = {
  turnoDiaInicio: string;
  turnoDiaTermino: string;
  turnoNoiteInicio: string;
  turnoNoiteTermino: string;
};

const HORARIOS_PADRAO: HorariosTurnoProducao = {
  turnoDiaInicio: "05:00",
  turnoDiaTermino: "21:59",
  turnoNoiteInicio: "22:00",
  turnoNoiteTermino: "04:59",
};

const TIMEZONE = BrazilDate.getTimezone();

/** 05:00 */
const INICIO_TURNO_DIA_MIN = 5 * 60;
/** 21:59 (inclusive) */
const FIM_TURNO_DIA_MIN = 21 * 60 + 59;
/** 22:00 */
const INICIO_TURNO_NOITE_MIN = 22 * 60;

function minutosDesdeMeiaNoiteSP(now: Date): number {
  const [hh, mm] = formatInTimeZone(now, TIMEZONE, "HH:mm").split(":");
  return Number(hh) * 60 + Number(mm);
}

export function addDaysISO(isoDate: string, days: number): string {
  return BrazilDate.subtractCalendarDaysFromISO(isoDate, -days);
}

export function getHojeISO(now: Date = new Date()): string {
  return formatInTimeZone(now, TIMEZONE, "yyyy-MM-dd");
}

/** Turno diurno: 05:00–21:59. Turno noturno: 22:00–04:59 (São Paulo). */
export function getTurnoProducao(now: Date = new Date()): TurnoProducao {
  const min = minutosDesdeMeiaNoiteSP(now);
  if (min >= INICIO_TURNO_DIA_MIN && min <= FIM_TURNO_DIA_MIN) {
    return "dia";
  }
  return "noite";
}

/**
 * Data da ficha em uso no turno atual:
 * - Diurno (05:00–21:59): hoje
 * - Noturno após 22:00: amanhã (lançamento antecipado)
 * - Noturno 00:00–04:59: hoje (mesma ficha aberta na noite anterior)
 */
export function getDataFichaOperacional(now: Date = new Date()): string {
  const hoje = getHojeISO(now);
  let data: string;
  if (getTurnoProducao(now) === "dia") {
    data = hoje;
  } else {
    const min = minutosDesdeMeiaNoiteSP(now);
    data = min >= INICIO_TURNO_NOITE_MIN ? addDaysISO(hoje, 1) : hoje;
  }
  return data;
}

/** @deprecated use getDataFichaOperacional */
export const getDataFichaPadrao = getDataFichaOperacional;

export function labelTurno(turno: TurnoProducao): string {
  return turno === "dia"
    ? "Diurno (05:00 às 21:59)"
    : "Noturno (22:00 às 04:59)";
}

export function labelTurnoCurto(turno: TurnoProducao): string {
  return turno === "dia" ? "DIA" : "NOITE";
}

export function formatHorarioTurnoAtual(
  turno: TurnoProducao,
  horarios?: HorariosTurnoProducao | null
): string {
  const h = horarios ?? HORARIOS_PADRAO;
  return turno === "dia"
    ? `${h.turnoDiaInicio} às ${h.turnoDiaTermino}`
    : `${h.turnoNoiteInicio} às ${h.turnoNoiteTermino}`;
}

export function labelTurnoComHorarios(
  turno: TurnoProducao,
  horarios?: HorariosTurnoProducao | null
): string {
  const periodo = turno === "dia" ? "Diurno" : "Noturno";
  return `${periodo} (${formatHorarioTurnoAtual(turno, horarios)})`;
}

export function isAmbienteLocalFrontend(): boolean {
  return process.env.NODE_ENV !== "production";
}

const TURNO_SIMULADO_STORAGE_KEY = "controle-producao-ficha-turno-simulado";

export function lerTurnoSimuladoStorage(): TurnoProducao | null {
  try {
    const value = sessionStorage.getItem(TURNO_SIMULADO_STORAGE_KEY);
    if (value === "dia" || value === "noite") return value;
  } catch {
    /* ignore */
  }
  return null;
}

export function gravarTurnoSimuladoStorage(turno: TurnoProducao): void {
  try {
    sessionStorage.setItem(TURNO_SIMULADO_STORAGE_KEY, turno);
  } catch {
    /* ignore */
  }
}

export function mensagemInstrutivaTurno(turno: TurnoProducao): string {
  if (turno === "noite") {
    return "1º Conferência: preencha SOBRA (turno NOITE).";
  }
  return "2º Produção: preencha PRODUÇÃO após a conferência assinada (turno DIA).";
}

/** Domingo à noite / madrugada de segunda na ficha operacional de segunda. */
export function deveUsarBaseSegundaSemanaAnterior(
  dataFichaISO: string,
  now: Date = new Date()
): boolean {
  if (isoWeekdayFromISO(dataFichaISO) !== 1) {
    return false;
  }
  if (dataFichaISO !== getDataFichaOperacional(now)) {
    return false;
  }
  if (getTurnoProducao(now) !== "noite") {
    return false;
  }
  const hoje = getHojeISO(now);
  const diaHoje = isoWeekdayFromISO(hoje);
  if (diaHoje === 7) {
    return true;
  }
  if (diaHoje === 1 && minutosDesdeMeiaNoiteSP(now) < INICIO_TURNO_NOITE_MIN) {
    return true;
  }
  return false;
}
